import React, { useState } from 'react';
import { X } from 'lucide-react';
import { SubscriptionPlan } from '../../../services/subscriptionPlanService';
import PlanForm from './PlanForm';

interface CreateEditPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => Promise<void>;
  plan?: SubscriptionPlan | null;
  isSubmitting: boolean;
}

const CreateEditPlanModal: React.FC<CreateEditPlanModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  plan,
  isSubmitting,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (formData: any) => {
    try {
      setError(null);
      setSuccess(null);
      await onSubmit(formData);
      setSuccess(plan ? 'Plan updated successfully' : 'Plan created successfully');
      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save plan';
      setError(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-[#16251D] border border-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-800 sticky top-0 bg-[#16251D]">
            <h2 className="text-2xl font-bold text-white">
              {plan ? 'Edit Plan' : 'Create New Plan'}
            </h2>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
                <p className="text-green-400 text-sm">{success}</p>
              </div>
            )}

            {/* Form */}
            <PlanForm
              plan={plan}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateEditPlanModal;
